import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Brain, AlertCircle, X, ArrowRight, CircleDot, MessageSquare,
  ShoppingCart, Zap, CheckSquare, MessageSquareQuote, XOctagon, Inbox,
} from 'lucide-react';
import { dashboardApi } from '@/api/dashboard';
import type { AgentSession, TimelineEvent, TimelineKind } from '@/api/types';
import { ago, timeOnly } from '@/lib/format';
import { cn } from '@/lib/cn';
import { AgentAvatar, displayAgent } from '@/components/AgentAvatar';
import { JsonViewer } from '@/components/JsonViewer';

const KIND_CONFIG: Record<TimelineKind, { icon: typeof CircleDot; color: string; border: string; dot: string; label: string }> = {
  SESSION_OPENED:   { icon: CircleDot,          color: 'text-text-faint', border: 'border-l-text-faint',  dot: 'bg-text-faint', label: 'SESSION OPENED' },
  AGENT_TURN:       { icon: MessageSquare,      color: 'text-kam-violet', border: 'border-l-kam-violet',  dot: 'bg-kam-violet', label: 'TURN' },
  AGENT_ERROR:      { icon: XOctagon,           color: 'text-kam-red',    border: 'border-l-kam-red',     dot: 'bg-kam-red',    label: 'ERROR' },
  TRADE_SUBMITTED:  { icon: ShoppingCart,       color: 'text-kam-green',  border: 'border-l-kam-green',   dot: 'bg-kam-green',  label: 'TRADE' },
  FILL:             { icon: CheckSquare,        color: 'text-kam-green',  border: 'border-l-kam-green',   dot: 'bg-kam-green',  label: 'FILL' },
  WATCH_TRIGGER:    { icon: Zap,                color: 'text-kam-amber',  border: 'border-l-kam-amber',   dot: 'bg-kam-amber',  label: 'WATCH TRIGGER' },
  SESSION_ARCHIVED: { icon: Inbox,              color: 'text-text-faint', border: 'border-l-text-faint',  dot: 'bg-text-faint', label: 'ARCHIVED' },
  EOD_REFLECTION:   { icon: MessageSquareQuote, color: 'text-kam-violet', border: 'border-l-kam-violet',  dot: 'bg-kam-violet', label: 'EOD REFLECTION' },
};

export function AgentChains() {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [date, setDate] = useState(today);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [drilldownLogId, setDrilldownLogId] = useState<string | null>(null);

  const sessions = useQuery({
    queryKey: ['agentSessions', date],
    queryFn: () => dashboardApi.agentSessions(date),
  });

  // Auto-select the first session when sessions arrive (or change).
  useEffect(() => {
    if (!sessions.data || sessions.data.length === 0) {
      setSelectedSessionId(null);
      return;
    }
    setSelectedSessionId((prev) => {
      if (prev && sessions.data.some((s) => s.sessionId === prev)) return prev;
      return sessions.data[0].sessionId;
    });
  }, [sessions.data]);

  const timeline = useQuery({
    queryKey: ['agentTimeline', selectedSessionId],
    queryFn: () => dashboardApi.agentTimeline(selectedSessionId!),
    enabled: !!selectedSessionId,
    refetchInterval: () => {
      const session = sessions.data?.find((s) => s.sessionId === selectedSessionId);
      return session?.status === 'OPEN' ? 15_000 : false;
    },
  });

  const selectedSession = sessions.data?.find((s) => s.sessionId === selectedSessionId);

  return (
    <div className="flex flex-col lg:flex-row min-h-[calc(100vh-120px)]">
      {/* Main column */}
      <div className="flex-1 min-w-0 p-4 lg:p-6 lg:pr-3 overflow-y-auto scroll">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-[10px] bg-surface-2 grid place-items-center text-text-dim">
            <Brain size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold">Agent Thought Chains</h1>
            <div className="text-xs text-text-faint">
              {sessions.data
                ? `${sessions.data.length} session${sessions.data.length === 1 ? '' : 's'} on ${date}`
                : sessions.isLoading ? 'loading…' : 'unavailable'}
            </div>
          </div>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="bg-surface-2 border border-[color:var(--border)] rounded-[10px] px-3 py-1.5 text-sm font-mono text-text"
          />
        </div>

        {sessions.isError && (
          <div className="card flex items-center gap-3 text-kam-red mb-4">
            <AlertCircle size={18} />
            <span>Couldn't load /api/agents/sessions</span>
          </div>
        )}

        {/* Agent picker */}
        {sessions.data && sessions.data.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-3 mb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {sessions.data.map((s) => (
              <SessionPill
                key={s.sessionId}
                session={s}
                active={selectedSessionId === s.sessionId}
                onClick={() => { setSelectedSessionId(s.sessionId); setDrilldownLogId(null); }}
              />
            ))}
          </div>
        )}

        {sessions.data?.length === 0 && (
          <div className="card max-w-2xl text-text-dim">
            No sessions for {date}. Sessions open at 08:00 KST on trading days.
          </div>
        )}

        {/* Selected session header */}
        {selectedSession && (
          <SessionHeaderCard session={selectedSession} />
        )}

        {/* Timeline */}
        {selectedSessionId && (
          <div className="mt-5">
            {timeline.isLoading && <div className="text-text-faint text-sm">Loading timeline…</div>}
            {timeline.isError && (
              <div className="card flex items-center gap-3 text-kam-red">
                <AlertCircle size={18} />
                <span>Couldn't load timeline</span>
              </div>
            )}
            {timeline.data && timeline.data.length === 0 && (
              <div className="card text-text-faint text-sm">No events recorded yet.</div>
            )}
            {timeline.data && timeline.data.length > 0 && (
              <Timeline
                events={timeline.data}
                onSelectLog={setDrilldownLogId}
                selectedLogId={drilldownLogId}
              />
            )}
          </div>
        )}
      </div>

      {/* Drilldown — side panel on desktop, bottom sheet on mobile */}
      {drilldownLogId && (
        <Drilldown logId={drilldownLogId} onClose={() => setDrilldownLogId(null)} />
      )}
    </div>
  );
}

// ─── Session pill (in the picker row) ─────────────────────────────────
function SessionPill({ session, active, onClick }: { session: AgentSession; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-3 py-1.5 rounded-chip border text-xs font-semibold whitespace-nowrap transition-colors',
        active
          ? 'bg-surface-3 border-strong text-text'
          : 'bg-surface border-[color:var(--border)] text-text-dim hover:text-text',
      )}
    >
      <AgentAvatar agent={session.agentName} size="sm" />
      <span>{displayAgent(session.agentName)}</span>
      <span className={cn(
        'inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider',
        session.status === 'OPEN' ? 'text-kam-green' :
        session.status === 'ARCHIVED' ? 'text-text-faint' :
        session.status === 'RESET' ? 'text-kam-amber' :
        'text-kam-red',
      )}>
        {session.status === 'OPEN' && <span className="pulse-dot" />}
        {session.status}
      </span>
      <span className="font-mono text-text-faint">{session.turnCount}</span>
    </button>
  );
}

// ─── Session header card ──────────────────────────────────────────────
function SessionHeaderCard({ session }: { session: AgentSession }) {
  return (
    <div className="card card-tight">
      <div className="flex items-start gap-3">
        <AgentAvatar agent={session.agentName} size="lg" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-base font-bold">{displayAgent(session.agentName)}</h2>
            <span className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-chip text-[10px] font-bold uppercase tracking-wider border',
              session.status === 'OPEN' ? 'bg-kam-green-soft text-kam-green border-kam-green/30' :
              session.status === 'ARCHIVED' ? 'bg-surface-3 text-text-dim border-[color:var(--border)]' :
              session.status === 'RESET' ? 'bg-kam-amber-soft text-kam-amber border-kam-amber/30' :
              'bg-kam-red-soft text-kam-red border-kam-red/30',
            )}>
              {session.status === 'OPEN' && <span className="pulse-dot" />}
              {session.status}
            </span>
            {session.hasReflection && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-chip text-[10px] font-semibold border bg-kam-violet-soft text-kam-violet border-kam-violet/30">
                <MessageSquareQuote size={9} /> reflection
              </span>
            )}
          </div>
          <div className="font-mono text-[10px] text-text-faint mt-1 truncate">{session.sessionId}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-3 border-t border-[color:var(--border)] text-xs">
        <Stat label="Turns" value={session.turnCount.toString()} />
        <Stat label="Input tokens" value={session.inputTokensTotal.toLocaleString()} />
        <Stat label="Output tokens" value={session.outputTokensTotal.toLocaleString()} />
        <Stat label="Opened" value={timeOnly(session.openedAt)} />
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="label">{label}</div>
      <div className="font-mono text-sm text-text">{value}</div>
    </div>
  );
}

// ─── Timeline ─────────────────────────────────────────────────────────
function Timeline({
  events, onSelectLog, selectedLogId,
}: { events: TimelineEvent[]; onSelectLog: (id: string) => void; selectedLogId: string | null }) {
  return (
    <div className="relative">
      {/* Vertical rail */}
      <div className="absolute left-[14px] top-3 bottom-3 w-px bg-[color:var(--border-strong)]" aria-hidden />
      <div className="flex flex-col gap-2">
        {events.map((e) => (
          <TimelineNode
            key={e.id}
            event={e}
            active={!!e.logId && e.logId === selectedLogId}
            onClick={() => e.logId && onSelectLog(e.logId)}
          />
        ))}
      </div>
    </div>
  );
}

function TimelineNode({ event, active, onClick }: { event: TimelineEvent; active: boolean; onClick: () => void }) {
  const cfg = KIND_CONFIG[event.kind] ?? KIND_CONFIG.AGENT_TURN;
  const Icon = cfg.icon;
  const clickable = !!event.logId;
  const isReflection = event.kind === 'EOD_REFLECTION';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={cn(
        'group relative grid grid-cols-[28px_1fr] gap-3 text-left transition-colors',
        clickable && 'cursor-pointer',
        !clickable && 'cursor-default',
      )}
    >
      {/* Dot */}
      <div className="flex justify-center pt-3 relative z-10">
        <div className={cn(
          'w-2.5 h-2.5 rounded-full ring-2 ring-bg flex-none',
          cfg.dot,
        )} />
      </div>
      {/* Card */}
      <div className={cn(
        'rounded-[12px] bg-surface-2 border border-[color:var(--border)] border-l-[3px] p-3',
        cfg.border,
        active && 'ring-1 ring-kam-violet/50',
        isReflection && 'bg-kam-grad-soft border-kam-violet/30',
        clickable && 'group-hover:bg-surface-3',
      )}>
        <div className="flex items-center gap-2 mb-1">
          <Icon size={12} className={cfg.color} />
          <span className={cn('text-[10px] font-bold uppercase tracking-[0.1em]', cfg.color)}>
            {cfg.label}
          </span>
          {event.at && (
            <span className="font-mono text-[10px] text-text-faint ml-auto">
              {timeOnly(event.at)}
            </span>
          )}
        </div>
        <div className="text-sm font-medium mb-0.5">{event.title}</div>
        {event.summary && (
          <div className={cn(
            'text-xs leading-relaxed',
            isReflection ? 'text-text italic' : 'text-text-dim',
          )}>
            {event.summary}
          </div>
        )}
        {event.meta.length > 0 && (
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[10px] text-text-faint">
            {event.meta.map((m) => (
              <span key={m.label}>
                <span className="opacity-70">{m.label}:</span>{' '}
                <span className="font-mono text-text-dim">{m.value}</span>
              </span>
            ))}
          </div>
        )}
        {clickable && (
          <div className="mt-2 text-[10px] text-text-faint inline-flex items-center gap-1 group-hover:text-text-dim">
            view raw <ArrowRight size={9} />
          </div>
        )}
      </div>
    </button>
  );
}

// ─── Drilldown panel ─────────────────────────────────────────────────
function Drilldown({ logId, onClose }: { logId: string; onClose: () => void }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['agentLog', logId],
    queryFn: () => dashboardApi.agentLogDetail(logId),
  });

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className="lg:hidden fixed inset-0 bg-black/60 z-40 animate-slide-up"
        onClick={onClose}
        aria-hidden
      />
      <aside className={cn(
        'lg:w-[480px] lg:flex-none lg:border-l lg:border-[color:var(--border)] lg:bg-bg-2 lg:relative lg:overflow-y-auto lg:scroll',
        'fixed inset-x-0 bottom-0 lg:static z-50',
        'bg-surface-2 lg:bg-bg-2 border-t lg:border-t-0 border-[color:var(--border)]',
        'rounded-t-[20px] lg:rounded-none',
        'max-h-[80vh] lg:max-h-none',
        'overflow-y-auto scroll',
        'animate-slide-up lg:animate-none',
      )}>
        {/* Sheet handle (mobile only) */}
        <div className="lg:hidden flex justify-center pt-2.5 pb-1">
          <div className="w-10 h-1 rounded-full bg-[color:var(--border-strong)]" />
        </div>

        <div className="px-5 py-3 flex items-center justify-between sticky top-0 bg-surface-2 lg:bg-bg-2 z-10 border-b border-[color:var(--border)]">
          <div className="text-sm font-semibold">Agent turn detail</div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-text-faint hover:text-text hover:bg-surface-3"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-4">
          {isLoading && <div className="text-text-faint text-sm">Loading…</div>}
          {isError && (
            <div className="text-kam-red text-sm flex items-center gap-2">
              <AlertCircle size={14} /> Couldn't load log {logId}
            </div>
          )}
          {data && (
            <>
              {/* Meta strip */}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-text-faint">
                {data.agent && <span><span className="opacity-70">Agent:</span> <span className="text-text-dim font-mono">{displayAgent(data.agent)}</span></span>}
                {data.model && <span><span className="opacity-70">Model:</span> <span className="text-text-dim font-mono">{data.model}</span></span>}
                {data.latencyMs != null && <span><span className="opacity-70">Latency:</span> <span className="text-text-dim font-mono">{data.latencyMs}ms</span></span>}
                {data.inputTokens != null && <span><span className="opacity-70">In:</span> <span className="text-text-dim font-mono">{data.inputTokens}</span></span>}
                {data.outputTokens != null && <span><span className="opacity-70">Out:</span> <span className="text-text-dim font-mono">{data.outputTokens}</span></span>}
                {data.thinkingTokens != null && data.thinkingTokens > 0 &&
                  <span><span className="opacity-70">Think:</span> <span className="text-text-dim font-mono">{data.thinkingTokens}</span></span>}
              </div>

              {data.errorMessage && (
                <Section title="Error">
                  <pre className="text-xs text-kam-red whitespace-pre-wrap break-words bg-kam-red-soft border border-kam-red/25 rounded-card-tight p-3">
                    {data.errorMessage}
                  </pre>
                </Section>
              )}

              {data.inputSummary && (
                <Section title="Input summary">
                  <div className="text-xs text-text-dim whitespace-pre-wrap leading-relaxed">
                    {data.inputSummary}
                  </div>
                </Section>
              )}

              {data.rawResponse && (
                <Section title="Raw response">
                  <pre className="text-xs text-text whitespace-pre-wrap break-words font-mono bg-bg-2 border border-[color:var(--border)] rounded-card-tight p-3 max-h-[40vh] overflow-auto scroll">
                    {data.rawResponse}
                  </pre>
                </Section>
              )}

              {data.parsedOutput && (
                <Section title="Parsed output (JSON)">
                  <JsonViewer json={data.parsedOutput} />
                </Section>
              )}

              <div className="text-[10px] text-text-faint font-mono pt-2 border-t border-[color:var(--border)]">
                {data.createdAt}{data.sessionId && ` · ${data.sessionId}`} · {ago(data.createdAt)}
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="label mb-1.5">{title}</div>
      {children}
    </div>
  );
}
